import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { TrendingUp, Film, Tv } from "lucide-react";
import { useReleaseModal } from "./ReleaseModal";

/**
 * TrendingNow — "Les plus regardés en ce moment"
 *
 * Source: TMDB trending (region FR, lang fr-FR), cached 1h backend-side.
 * NOT a scientific audience ranking — reflects TMDB page views, which
 * correlates with but doesn't equal streaming consumption.
 * Clear disclaimer in the UI to stay honest with readers.
 *
 * Tabs: Films / Séries
 * Window: Jour / Semaine
 * Shows a Top 10 as a numbered scrollable rail.
 */

const WINDOW_LABELS = { day: "24 h", week: "7 jours" };

export const TrendingNow = () => {
  const [kind, setKind] = useState("movie"); // movie | tv
  const [win, setWin] = useState("day"); // day | week
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    api
      .get("/trending", { params: { kind, window: win, limit: 10 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [kind, win]);

  return (
    <section
      className="max-w-[1300px] mx-auto px-5 md:px-10 mt-16"
      data-testid="home-trending-section"
    >
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5" />
            Tendances
          </div>
          <h2 className="font-display text-3xl md:text-4xl mt-1">
            Les plus regardés en ce moment
          </h2>
          <p className="text-xs text-neutral-500 mt-1.5">
            Classement d'intérêt mondial — source&nbsp;TMDB, mis à jour quotidiennement.
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2" data-testid="trending-controls">
          <TabButton
            active={kind === "movie"}
            onClick={() => setKind("movie")}
            testid="trending-tab-movie"
            icon={Film}
          >
            Films
          </TabButton>
          <TabButton
            active={kind === "tv"}
            onClick={() => setKind("tv")}
            testid="trending-tab-tv"
            icon={Tv}
          >
            Séries
          </TabButton>
          <div className="h-5 w-px bg-neutral-300 mx-1" aria-hidden="true" />
          <PillButton
            active={win === "day"}
            onClick={() => setWin("day")}
            testid="trending-window-day"
          >
            {WINDOW_LABELS.day}
          </PillButton>
          <PillButton
            active={win === "week"}
            onClick={() => setWin("week")}
            testid="trending-window-week"
          >
            {WINDOW_LABELS.week}
          </PillButton>
        </div>
      </div>

      {loading ? (
        <TrendingRail skeleton />
      ) : error ? (
        <div className="text-sm text-neutral-500 py-8 text-center" data-testid="trending-error">
          Tendances momentanément indisponibles.
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-neutral-500 py-8 text-center">Aucune tendance pour l'instant.</div>
      ) : (
        <TrendingRail items={items} />
      )}
    </section>
  );
};

const TabButton = ({ children, active, onClick, testid, icon: Icon }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-medium px-3 py-1.5 border transition-colors ${
      active
        ? "bg-[var(--red)] text-white border-[var(--red)]"
        : "bg-white text-neutral-700 border-neutral-300 hover:border-[var(--red)] hover:text-[var(--red)]"
    }`}
  >
    {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
    {children}
  </button>
);

const PillButton = ({ children, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    className={`text-[11px] uppercase tracking-[0.2em] font-medium px-2.5 py-1 rounded-full transition-colors ${
      active
        ? "bg-neutral-900 text-white"
        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
    }`}
  >
    {children}
  </button>
);

const TrendingRail = ({ items = [], skeleton }) => (
  <div
    className="flex gap-4 overflow-x-auto pb-4 -mx-5 px-5 md:mx-0 md:px-0 snap-x snap-mandatory md:snap-none"
    data-testid="trending-rail"
    style={{ scrollbarWidth: "thin" }}
  >
    {(skeleton ? Array.from({ length: 10 }) : items).map((it, idx) => (
      <TrendingCard key={skeleton ? idx : `${it.type}-${it.tmdb_id}`} item={it} rank={idx + 1} skeleton={skeleton} />
    ))}
  </div>
);

const TrendingCard = ({ item, rank, skeleton }) => {
  const { openRelease } = useReleaseModal();
  return (
  <article
    className={`flex-shrink-0 w-[180px] md:w-[200px] snap-start ${skeleton ? "" : "cursor-pointer group"}`}
    data-testid={skeleton ? "trending-card-skeleton" : "trending-card"}
    {...(skeleton ? {} : {
      role: "button",
      tabIndex: 0,
      "aria-label": `Voir le détail de ${item.title}`,
      onClick: () => openRelease(item, { external: true }),
      onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openRelease(item, { external: true })),
    })}
  >
    <div className="relative">
      {/* Giant rank number — editorial poster-style */}
      <div
        className="absolute -left-2 -top-2 z-10 text-[72px] md:text-[88px] leading-none font-display font-bold select-none pointer-events-none"
        style={{ color: "var(--red)", WebkitTextStroke: "2px white", textShadow: "0 2px 8px rgba(0,0,0,0.25)" }}
        aria-hidden="true"
      >
        {rank}
      </div>
      <div className="aspect-[2/3] bg-neutral-100 overflow-hidden">
        {skeleton ? (
          <div className="h-full w-full animate-pulse bg-neutral-200" />
        ) : item?.poster_url ? (
          <img
            src={item.poster_url}
            alt={item.title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-xs text-neutral-400">
            Pas d'affiche
          </div>
        )}
      </div>
    </div>
    {!skeleton && (
      <div className="mt-2.5 pl-1">
        <div className="text-sm font-medium leading-snug line-clamp-2" title={item.title}>
          {item.title}
        </div>
        <div className="text-[11px] text-neutral-500 mt-0.5 uppercase tracking-wide">
          {item.year || "—"}
          {item.vote_average ? ` · ★ ${item.vote_average}` : ""}
        </div>
      </div>
    )}
  </article>
  );
};

export default TrendingNow;
